/*
Origin Core 1.0

called from /snippets/predictive-search.liquid

DEVELOPER NOTE
--------------------
any changes to this file should be noted here, and also noted in the following wiki page:
https://unifiedco.atlassian.net/wiki/spaces/ORIGIN/pages/1413414932/Core+File+Changes+1.0

SUMMARY
--------------------
Nosto result cards pass the search term to the shared card for initial swatch selection.
This script defines a custom web component <predictive-search-component> that manages predictive search functionality for Shopify. 
It handles user input, fetches and displays search results via AJAX, supports keyboard navigation, and manages recently viewed products. 
The component updates the UI with search results or recently viewed items, handles dialog interactions, and provides accessibility features. 
It is registered as a custom element if not already defined.

*/

import { Component } from '@theme/component';
import { debounce, onAnimationEnd, prefersReducedMotion } from '@theme/utilities';
import { sectionRenderer } from '@theme/section-renderer';
import { morph } from '@theme/morph';
import { ThemeEvents } from '@theme/events';
import { RecentlyViewed } from '@theme/recently-viewed-products';
import { DialogCloseEvent, DialogComponent, DialogOpenEvent } from '@theme/dialog';

/**
 * A custom element that allows the user to search for resources available on the store.
 *
 * @typedef {object} Refs
 * @property {HTMLInputElement} searchInput - The search input element.
 * @property {HTMLElement} predictiveSearchResults - The predictive search results container.
 * @property {HTMLElement} resetButton - The reset button element.
 * @property {HTMLElement[]} [resultsItems] - The search results items elements.
 * @property {HTMLElement} [recentlyViewedWrapper] - The recently viewed products wrapper.
 * @property {HTMLElement[]} [recentlyViewedTitle] - The recently viewed title elements.
 * @property {HTMLElement[]} [recentlyViewedItems] - The recently viewed product items.
 * @extends {Component<Refs>}
 */
class PredictiveSearchComponent extends Component {
  requiredRefs = ['searchInput', 'predictiveSearchResults', 'resetButton'];

  static recentSearchesStorageKey = 'origin-nosto-recent-searches';

  #controller = new AbortController();
  #emptyStateMarkup = '';
  #nostoProductCardMarkupCache = new Map();

  /**
   * @type {AbortController | null}
   */
  #activeFetch = null;

  #resizeObserver = new ResizeObserver((entries) => {
    for (const entry of entries) {
      this.style.setProperty('--predictive-search-results-height', `${entry.contentRect.height}px`);
    }
  });

  /**
   * Get the dialog component.
   * @returns {DialogComponent | null} The dialog component.
   */
  get dialog() {
    return this.closest('dialog-component');
  }

  connectedCallback() {
    super.connectedCallback();

    const { dialog } = this;
    const { signal } = this.#controller;
    this.#emptyStateMarkup = this.refs.predictiveSearchResults.innerHTML.trim();

    if (this.refs.searchInput.value.length > 0) {
      this.#showResetButton();
    }

    this.refs.form?.addEventListener('submit', this.#handleFormSubmit, { signal });

    if (dialog) {
      document.addEventListener('keydown', this.#handleKeyboardShortcut, { signal });
      dialog.addEventListener(DialogOpenEvent.eventName, this.#handleDialogOpen, { signal });
      dialog.addEventListener(DialogCloseEvent.eventName, this.#handleDialogClose, { signal });

      this.addEventListener('click', this.#handleModalClick, { signal });
    } else {
      document.addEventListener(ThemeEvents.megaMenuHover, this.#blurSearch, { signal });
    }

    this.#getRecentlyViewed();
    this.#syncOpenStateRecentSearches();
    this.#loadNostoTrendingPlacement();
    this.#hideViewAllButton();

    const results = this.refs.predictiveSearchResults.firstElementChild;

    if (results) {
      this.#resizeObserver.observe(results);
    }

    this.#syncMobileUtilityLinks();
  }

  /**
   * Handles clicks within the predictive search modal to maintain focus on the input
   * @param {MouseEvent} event - The mouse event
   */
  #handleModalClick = (event) => {
    const target = /** @type {HTMLElement} */ (event.target);
    this.#recordNostoResultClick(target);
    const isInteractiveElement =
      target instanceof HTMLButtonElement ||
      target instanceof HTMLAnchorElement ||
      target instanceof HTMLInputElement ||
      target.closest('button') ||
      target.closest('a') ||
      target.closest('input');

    if (!isInteractiveElement && this.refs.searchInput) {
      this.refs.searchInput.focus();
    }
  };

  /**
   * Tracks submitted Nosto autocomplete searches when the browser form submit path is used.
   * @param {SubmitEvent} event
   */
  #handleFormSubmit = (event) => {
    if (!this.#usesNostoSearch) return;

    const query = this.refs.searchInput.value.trim();
    if (!query) return;

    event.preventDefault();
    this.#saveRecentSearch(query);
    this.#submitSearch(query);
  };

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#controller.abort();
    this.#resizeObserver.disconnect();
  }

  /**
   * Handles the CMD+K key combination.
   * @param {KeyboardEvent} event - The keyboard event.
   */
  #handleKeyboardShortcut = (event) => {
    if (event.metaKey && event.key === 'k') {
      this.dialog?.toggleDialog();
    }
  };

  /**
   * Handles the dialog open event.
   */
  #handleDialogOpen = () => {
    this.#syncMobileUtilityLinks();

    const { searchInput } = this.refs;
    searchInput.focus({ preventScroll: true });

    const inputLength = searchInput.value.length;
    searchInput.setSelectionRange(inputLength, inputLength);
    this.expandSearch();
  };

  /**
   * Handles the dialog close event.
   */
  #handleDialogClose = () => {
    this.#resetSearch();
  };

  expandSearch = () => {
    // Add the expanded class to the search component
    this.classList.add('predictive-search--expanded');
    if (this.dataset.activeColorScheme) {
      const target = this.dialog ?? this;
      target.classList.add(`color-${this.dataset.activeColorScheme}`);
    }
  };

  get #allResultsItems() {
    const containers = Array.from(
      this.querySelectorAll(
        '.predictive-search-results__wrapper-queries, ' +
          '.predictive-search-results__wrapper-products, ' +
          '.predictive-search-results__list'
      )
    );

    const allItems = containers
      .flatMap((container) => {
        if (container.classList.contains('predictive-search-results__wrapper-products')) {
          return Array.from(container.querySelectorAll('.predictive-search-results__card'));
        }
        return Array.from(container.querySelectorAll('[ref="resultsItems[]"], .predictive-search-results__card'));
      })
      .filter((item) => item instanceof HTMLElement);

    return /** @type {HTMLElement[]} */ (allItems);
  }

  /**
   * Track whether the last interaction was keyboard-based
   * @type {boolean}
   */
  #isKeyboardNavigation = false;

  get #currentIndex() {
    return this.#allResultsItems?.findIndex((item) => item.getAttribute('aria-selected') === 'true') ?? -1;
  }

  set #currentIndex(index) {
    if (!this.#allResultsItems?.length) return;

    this.#allResultsItems.forEach((item) => {
      item.classList.remove('keyboard-focus');
    });

    for (const [itemIndex, item] of this.#allResultsItems.entries()) {
      if (itemIndex === index) {
        item.setAttribute('aria-selected', 'true');

        if (this.#isKeyboardNavigation) {
          item.classList.add('keyboard-focus');
        }
        item.scrollIntoView({ behavior: prefersReducedMotion() ? 'instant' : 'smooth', block: 'nearest' });
      } else {
        item.removeAttribute('aria-selected');
      }
    }
    this.refs.searchInput.focus();
  }

  get #currentItem() {
    return this.#allResultsItems?.[this.#currentIndex];
  }

  get #usesNostoSearch() {
    return this.dataset.searchProvider === 'nosto' && typeof window.nostojs === 'function';
  }

  get #usesNostoTrending() {
    return this.dataset.nostoTrendingEnabled === 'true' && this.#nostoTrendingPlacementId !== '' && typeof window.nostojs === 'function';
  }

  get #nostoTrendingPlacementId() {
    return (this.dataset.nostoTrendingPlacementId || '').trim();
  }

  /**
   * Navigate through the predictive search results using arrow keys or close them with the Escape key.
   * @param {KeyboardEvent} event - The keyboard event.
   */
  onSearchKeyDown = (event) => {
    if (event.key === 'Escape') {
      this.#resetSearch();
      return;
    }

    if (!this.#allResultsItems?.length || event.key === 'ArrowLeft' || event.key === 'ArrowRight') {
      return;
    }

    const currentIndex = this.#currentIndex;
    const totalItems = this.#allResultsItems.length;

    switch (event.key) {
      case 'ArrowDown':
        this.#isKeyboardNavigation = true;
        event.preventDefault();
        this.#currentIndex = currentIndex < totalItems - 1 ? currentIndex + 1 : 0;
        break;

      case 'Tab':
        if (event.shiftKey) {
          this.#isKeyboardNavigation = true;
          event.preventDefault();
          this.#currentIndex = currentIndex > 0 ? currentIndex - 1 : totalItems - 1;
        } else {
          this.#isKeyboardNavigation = true;
          event.preventDefault();
          this.#currentIndex = currentIndex < totalItems - 1 ? currentIndex + 1 : 0;
        }
        break;

      case 'ArrowUp':
        this.#isKeyboardNavigation = true;
        event.preventDefault();
        this.#currentIndex = currentIndex > 0 ? currentIndex - 1 : totalItems - 1;
        break;

      case 'Enter':
        const singleResultContainer = this.refs.predictiveSearchResults.querySelector('[data-single-result-url]');
        if (singleResultContainer instanceof HTMLElement && singleResultContainer.dataset.singleResultUrl) {
          event.preventDefault();
          window.location.href = singleResultContainer.dataset.singleResultUrl;
          return;
        }

        if (this.#currentIndex >= 0) {
          event.preventDefault();
          this.#currentItem?.querySelector('a')?.click();
        } else {
          event.preventDefault();
          this.#submitSearch(this.refs.searchInput.value);
        }
        break;
    }
  };

  /**
   * Clears the recently viewed products.
   * @param {Event} event - The event.
   */
  clearRecentlyViewedProducts(event) {
    event.stopPropagation();

    RecentlyViewed.clearProducts();

    const { recentlyViewedItems, recentlyViewedTitle, recentlyViewedWrapper } = this.refs;

    const allRecentlyViewedElements = [...(recentlyViewedItems || []), ...(recentlyViewedTitle || [])];

    if (allRecentlyViewedElements.length === 0) {
      return;
    }

    if (recentlyViewedWrapper) {
      recentlyViewedWrapper.classList.add('removing');

      onAnimationEnd(recentlyViewedWrapper, () => {
        recentlyViewedWrapper.remove();
      });
    }
  }

  /**
   * Reset the search state.
   * @param {boolean} [keepFocus=true] - Whether to keep focus on input after reset
   */
  resetSearch = debounce((keepFocus = true) => {
    if (keepFocus) {
      this.refs.searchInput.focus();
    }
    this.#resetSearch();
  }, 100);

  /**
   * Debounce the search handler to fetch and display search results based on the input value.
   * Reset the current selection index and close results if the search term is empty.
   */
  search = debounce((event) => {
    const searchTerm = this.refs.searchInput.value.trim();
    this.#currentIndex = -1;

    if (!searchTerm.length) {
      this.#resetSearch();
      return;
    }

    // Native search-input clear actions can omit inputType, but we still want empty values to reset.
    if (!event.inputType) return;

    this.#showResetButton();
    this.#updateViewAllButton(searchTerm);

    if (this.#usesNostoSearch) {
      this.#getNostoSearchResults(searchTerm);
      return;
    }

    this.#getSearchResults(searchTerm);
  }, 200);

  /**
   * Resets scroll positions for search results containers
   */
  #resetScrollPositions() {
    requestAnimationFrame(() => {
      const resultsInner = this.refs.predictiveSearchResults.querySelector('.predictive-search-results__inner');
      if (resultsInner instanceof HTMLElement) {
        resultsInner.scrollTop = 0;
      }

      const formContent = this.querySelector('.predictive-search-form__content');
      if (formContent instanceof HTMLElement) {
        formContent.scrollTop = 0;
      }
    });
  }

  /**
   * Fetch autocomplete results from Nosto while keeping Origin's search modal shell.
   * @param {string} searchTerm - The term to search for
   */
  async #getNostoSearchResults(searchTerm) {
    const { predictiveSearchResults } = this.refs;
    const abortController = this.#createAbortController();

    try {
      const api = await new Promise((resolve, reject) => {
        const timeoutId = window.setTimeout(() => reject(new Error('Timed out waiting for Nosto JS API.')), 4000);

        window.nostojs((nostoApi) => {
          window.clearTimeout(timeoutId);
          resolve(nostoApi);
        });
      });

      const search = this.#getNostoSearchMethod(api);

      if (!search) {
        throw new Error('Nosto search API method was not found.');
      }

      const response = await search(
        {
          query: searchTerm,
          products: {
            fields: [
              'productId',
              'name',
              'url',
              'imageUrl',
              'thumbUrl',
              'price',
              'listPrice',
              'brand',
              'customFields.key',
              'customFields.value'
            ],
            size: 4,
          },
          keywords: {
            fields: ['keyword', '_highlight.keyword'],
            size: 5,
          },
          categories: {
            fields: ['name', 'fullName', 'url', 'urlPath'],
            size: 3,
          },
        },
        { track: 'autocomplete' }
      );

      if (abortController.signal.aborted) return;

      const contentResults = await this.#getShopifyContentResults(searchTerm);
      if (abortController.signal.aborted) return;

      const resultsMarkup = this.#renderNostoSearchResults(searchTerm, response, contentResults);
      const resultsElement = new DOMParser().parseFromString(resultsMarkup, 'text/html').body.firstElementChild;

      if (!resultsElement) return;

      const hasSearchData =
        (Array.isArray(response?.products?.hits) && response.products.hits.length > 0) ||
        (Array.isArray(response?.keywords?.hits) && response.keywords.hits.length > 0) ||
        (Array.isArray(response?.categories?.hits) && response.categories.hits.length > 0) ||
        contentResults.count > 0;

      morph(predictiveSearchResults, resultsElement);
      predictiveSearchResults.classList.add('nosto-autocomplete');
      this.#hideViewAllButton();
      this.#hydrateNostoAutocompleteCards();
      this.#loadNostoTrendingPlacement();
      this.#resetScrollPositions();
    } catch (error) {
      if (abortController.signal.aborted) return;
      console.warn('Nosto autocomplete unavailable, falling back to Shopify predictive search.', error);
      this.#getSearchResults(searchTerm);
    }
  }

  /**
   * Nosto storefront versions can expose search either as api.search(...) or api.search.query(...).
   * @param {any} api
   * @returns {((request: any, options: any) => Promise<any>) | null}
   */
  #getNostoSearchMethod(api) {
    if (typeof api?.search === 'function') {
      return api.search.bind(api);
    }

    if (typeof api?.search?.query === 'function') {
      return api.search.query.bind(api.search);
    }

    return null;
  }

  /**
   * Build the autocomplete dropdown markup from a Nosto Search response.
   * @param {string} searchTerm
   * @param {any} response
   * @param  [contentResults]
   * @returns {string}
   */
  #renderNostoSearchResults(searchTerm, response, contentResults = { markup: '', count: 0 }) {
    const products = Array.isArray(response?.products?.hits) ? response.products.hits : [];
    const keywords = Array.isArray(response?.keywords?.hits) ? response.keywords.hits : [];
    const categories = Array.isArray(response?.categories?.hits) ? response.categories.hits : [];
    const totalProducts = Number(response?.products?.total);
    const totalCategories = Number(response?.categories?.total);
    const productTotal = Number.isFinite(totalProducts) ? totalProducts : products.length;
    const categoryTotal = Number.isFinite(totalCategories) ? totalCategories : categories.length;
    const contentResultsMarkup = this.#getStringValue(contentResults.markup).trim();
    const contentResultsCount = Number.isFinite(contentResults.count) ? contentResults.count : 0;
    const resultCount = productTotal + keywords.length + categoryTotal + contentResultsCount;
    const searchUrl = this.#getSearchUrl(searchTerm);
    const hasMatches = products.length > 0;
    const hasKeywordSuggestions = keywords.length > 0;
    const hasCategorySuggestions = categories.length > 0;
    const hasContentSuggestions = contentResultsMarkup !== '';
    const hasSearchData = hasMatches || hasKeywordSuggestions || hasCategorySuggestions || hasContentSuggestions;
    const hasNoResults = !hasMatches && !hasKeywordSuggestions && !hasCategorySuggestions && !hasContentSuggestions;
    const recentSearches = this.#getRecentSearches(searchTerm);
    const configuredSuggestions = this.#getConfiguredSuggestedSearches();
    const helpHeading = this.#getNoResultsHelpHeading();
    const helpItems = this.#getNoResultsHelpLinksMarkup();
    const layoutClasses = ['nosto-autocomplete__layout'];
    const suggestionListClasses = ['nosto-autocomplete__suggestion-list'];
    const suggestionItems = hasNoResults
      ? helpItems
      : hasKeywordSuggestions
        ? keywords.map((keyword) => this.#renderNostoKeyword(keyword, searchTerm)).join('')
        : hasCategorySuggestions
          ? ''
        : recentSearches.length
          ? recentSearches.map((query) => this.#renderRecentSearch(query)).join('')
          : configuredSuggestions.length
            ? configuredSuggestions.map((query) => this.#renderRecentSearch(query)).join('')
            : this.#renderNostoKeyword({ keyword: searchTerm }, searchTerm);
    const singleProduct =
      products.length === 1 && keywords.length === 0 && categories.length === 0 ? this.#getNostoProductUrl(products[0]) : '';

    const productsHtml = products.length ? products.map((product) => this.#renderNostoProduct(product, searchTerm)).join('') : '';
    const categoriesHtml = categories.length ? categories.map((category) => this.#renderNostoCategory(category)).join('') : '';

    const sideHeading = hasNoResults
      ? helpHeading
      : hasKeywordSuggestions
        ? 'Suggestions'
        : recentSearches.length
          ? 'Recent Searches'
          : 'Suggested Searches';
    const productHeading = hasMatches ? 'Search Results' : 'Trending';
    const noResultsMessage = hasNoResults
      ? `<p class="nosto-autocomplete__error body-regular">We’re sorry, there are no results for “${this.#escapeHTML(searchTerm)}”</p>`
      : '';
    if (hasKeywordSuggestions && !hasNoResults) {
      layoutClasses.push('nosto-autocomplete__layout--keywords');
      suggestionListClasses.push('nosto-autocomplete__suggestion-list--double');
    }

    if (hasNoResults) {
      layoutClasses.push('nosto-autocomplete__layout--no-results');
    }

    const productsSection = hasMatches
      ? `<ul class="nosto-autocomplete__product-grid" role="list" aria-label="Products">
          ${productsHtml}
        </ul>`
      : this.#getOpenStateProductsMarkup();
    const suggestionsSection = suggestionItems
      ? `<div class="nosto-autocomplete__suggestion-group">
          <div class="nosto-autocomplete__section-head">
            <p class="nosto-autocomplete__heading title-4 mb-4">${sideHeading}</p>
          </div>
          <ul class="${suggestionListClasses.join(' ')}" role="list" aria-label="Search suggestions">
            ${suggestionItems}
          </ul>
        </div>`
      : '';
    const categoriesSection = categoriesHtml
      ? `<div class="nosto-autocomplete__category-group">
          <div class="nosto-autocomplete__section-head">
            <p class="nosto-autocomplete__heading title-4 mb-4">Collections</p>
          </div>
          <ul class="nosto-autocomplete__category-list" role="list" aria-label="Collections">
            ${categoriesHtml}
          </ul>
        </div>`
      : '';
    const contentSection = hasContentSuggestions ? contentResultsMarkup : '';
    const resultsLink = hasSearchData
      ? `<a class="cta nosto-autocomplete__view-all alt-body-tiny !border-0 !capitalize anim-link-reverse" href="${this.#escapeAttribute(searchUrl)}" data-nosto-view-all>
          All Results
        </a>`
      : '';

    return `
      <div
        id="predictive-search-results"
        class="predictive-search-dropdown nosto-autocomplete"
        role="dialog"
        aria-label="Search results"
        aria-expanded="true"
        data-transition-container
      >
        <div class="predictive-search-results__inner" data-search-results data-nosto-search-results>
          <div class="sr-only" role="status" aria-live="polite">
            ${resultCount} results for ${this.#escapeHTML(searchTerm)}
          </div>
          ${noResultsMessage}
          <div class="${layoutClasses.join(' ')}">
            <div class="nosto-autocomplete__suggestions">
              ${suggestionsSection}
              ${categoriesSection}
              ${contentSection}
            </div>
            <div class="nosto-autocomplete__products">
              <div class="nosto-autocomplete__products-head">
                <p class="nosto-autocomplete__heading title-4 mb-4">${productHeading}</p>
                ${resultsLink}
              </div>
              ${productsSection}
            </div>
          </div>
          ${
            singleProduct
              ? `<div data-single-result-url="${this.#escapeAttribute(singleProduct)}"></div>`
              : ''
          }
        </div>
      </div>
    `;
  }

  #renderStaticSuggestionLink(label, url) {
    const normalizedLabel = this.#getStringValue(label).trim();
    const normalizedUrl = this.#normalizeUrl(url);

    if (!normalizedLabel || normalizedUrl === '#') return '';

    return `
      <li ref="resultsItems[]" role="listitem" on:keydown="/onSearchKeyDown">
        <a
          class="nosto-autocomplete__suggestion-link"
          href="${this.#escapeAttribute(normalizedUrl)}"
        >
          ${this.#escapeHTML(normalizedLabel)}
        </a>
      </li>
    `;
  }

  /**
   * Fetch Shopify page and blog article results for the Nosto autocomplete side column.
   * @param {string} searchTerm
   * @returns {Promise<{ markup: string, count: number }>}
   */
  async #getShopifyContentResults(searchTerm) {
    const sectionId = 'predictive-search-content';
    const url = new URL(Theme.routes.predictive_search_url, location.origin);
    url.searchParams.set('q', searchTerm);
    url.searchParams.set('resources[type]', 'page,article');
    url.searchParams.set('resources[limit_scope]', 'each');
    url.searchParams.set('resources[limit]', '3');
    url.searchParams.set('resources[options][fields]', 'title,tag');

    try {
      const markup = await sectionRenderer.getSectionHTML(sectionId, false, url);
      const parsedMarkup = new DOMParser().parseFromString(markup, 'text/html');
      const results = parsedMarkup.querySelector('[data-nosto-content-results]');

      if (!(results instanceof HTMLElement)) {
        return { markup: '', count: 0 };
      }

      const renderedResultsCount = results.querySelectorAll('.nosto-autocomplete__content-link').length;

      return {
        markup: results.innerHTML.trim(),
        count: Number(results.dataset.resultsCount) || renderedResultsCount,
      };
    } catch (error) {
      console.warn('Shopify page/article predictive search unavailable.', error);
      return { markup: '', count: 0 };
    }
  }

  /**
   * @param {any} category
   * @returns {string}
   */
  #renderNostoCategory(category) {
    const categoryName = this.#getStringValue(category?.name ?? category?.fullName).trim();
    const categoryUrl = this.#getNostoCategoryUrl(category);

    if (!categoryName || categoryUrl === '#') return '';

    return `
      <li ref="resultsItems[]" role="listitem" on:keydown="/onSearchKeyDown">
        <a
          class="nosto-autocomplete__category-link"
          href="${this.#escapeAttribute(categoryUrl)}"
        >
          ${this.#escapeHTML(categoryName)}
        </a>
      </li>
    `;
  }

  #getNoResultsHelpHeading() {
    const heading = this.#getStringValue(this.dataset.noResultsHelpHeading).trim();
    return heading || 'Need help?';
  }

  #getNoResultsHelpLinks() {
    const links = [];

    for (let i = 1; i <= 3; i += 1) {
      const label = this.#getStringValue(this.dataset[`noResultsHelpLink${i}Label`]).trim();
      const url = this.#getStringValue(this.dataset[`noResultsHelpLink${i}Url`]).trim();
      if (!label || !url) continue;
      links.push({ label, url });
    }

    return links;
  }

  #getNoResultsHelpLinksMarkup() {
    const template = this.querySelector('[data-no-results-help-links-template]');
    if (template instanceof HTMLTemplateElement) {
      return template.innerHTML.trim();
    }

    return this.#getNoResultsHelpLinks()
      .map((link) => this.#renderStaticSuggestionLink(link.label, link.url))
      .join('');
  }


  /**
   * @param {any} keyword
   * @param {string} fallback
   * @returns {string}
   */
  #renderNostoKeyword(keyword, fallback) {
    const keywordText = this.#getStringValue(keyword?.keyword ?? keyword?._highlight?.keyword ?? fallback);
    const highlightedKeyword = this.#getHighlightedKeywordMarkup(keyword, keywordText);
    const searchUrl = this.#getSearchUrl(keywordText);

    return `
      <li ref="resultsItems[]" role="listitem" on:keydown="/onSearchKeyDown">
        <a
          class="nosto-autocomplete__suggestion-link"
          href="${this.#escapeAttribute(searchUrl)}"
          data-nosto-keyword="${this.#escapeAttribute(keywordText)}"
        >
          ${highlightedKeyword}
        </a>
      </li>
    `;
  }

  /**
   * @param {any} keyword
   * @param {string} fallback
   * @returns {string}
   */
  #getHighlightedKeywordMarkup(keyword, fallback) {
    const highlight = this.#getStringValue(keyword?._highlight?.keyword);
    if (!highlight) return this.#escapeHTML(fallback);

    return this.#escapeHTML(highlight)
      .replace(/&lt;strong&gt;/gi, '<strong>')
      .replace(/&lt;\/strong&gt;/gi, '</strong>');
  }

  /**
   * @param {string} query
   * @returns {string}
   */
  #renderRecentSearch(query) {
    const searchUrl = this.#getSearchUrl(query);

    return `
      <li ref="resultsItems[]" role="listitem" on:keydown="/onSearchKeyDown">
        <a
          class="nosto-autocomplete__suggestion-link"
          href="${this.#escapeAttribute(searchUrl)}"
          data-nosto-keyword="${this.#escapeAttribute(query)}"
        >
          ${this.#escapeHTML(query)}
        </a>
      </li>
    `;
  }

  #loadNostoTrendingPlacement() {
    if (!this.#usesNostoTrending) return;

    const selector =
      typeof CSS !== 'undefined' && typeof CSS.escape === 'function'
        ? `#${CSS.escape(this.#nostoTrendingPlacementId)}.nosto_element`
        : `#${this.#nostoTrendingPlacementId}.nosto_element`;

    const placement = this.refs.predictiveSearchResults.querySelector(selector);

    if (!(placement instanceof HTMLElement)) return;

    window.nostojs((api) => {
      if (typeof api.loadRecommendations === 'function') {
        api.loadRecommendations();
      }
    });
  }

  /**
   * @returns {string[]}
   */
  #getConfiguredSuggestedSearches() {
    const rawSuggestions = this.dataset.suggestedSearches;
    if (!rawSuggestions) return [];

    return rawSuggestions
      .split(',')
      .map((query) => query.trim())
      .filter(Boolean)
      .slice(0, 3);
  }

  /**
   * @returns {string}
   */
  #getOpenStateProductsMarkup() {
    const template = this.querySelector('[data-open-search-products-template]');
    if (!(template instanceof HTMLTemplateElement)) return '';

    return template.innerHTML.trim();
  }

  /**
   * @returns {string}
   */
  #getNostoCartIconMarkup() {
    const template = this.querySelector('[data-nosto-cart-icon-template]');
    if (!(template instanceof HTMLTemplateElement)) return '';

    return template.innerHTML.trim();
  }

  /**
   * @returns {string}
   */
  #getNostoWishlistIconMarkup() {
    const template = this.querySelector('[data-nosto-wishlist-icon-template]');
    if (!(template instanceof HTMLTemplateElement)) return '';

    return template.innerHTML.trim();
  }

  /**
   * @param {any} product
   * @returns {string}
   */
  #renderNostoProduct(product, searchTerm) {
    const productUrl = this.#getNostoProductUrl(product);
    const productId = product?.productId || product?.id || productUrl;
    const productName = this.#getStringValue(product?.name ?? product?.title);
    const productLabels = [
      this.#getNostoCustomField(product, 'custom-product_label'),
      this.#getNostoCustomField(product, 'custom-product_label_2'),
    ].filter(Boolean);
    const productSubtitle = this.#getStringValue(
      this.#getNostoCustomField(product, 'sub_title', 'subTitle') ?? product?.subtitle ?? product?.subTitle
    ).trim();
    const imageUrl = this.#getNostoImageUrl(product);
    const price = this.#formatNostoPrice(product?.price);
    const listPrice = this.#formatNostoPrice(product?.listPrice);
    const showListPrice = listPrice && Number(product?.listPrice) > Number(product?.price);
    const quickAddIcon = this.#getNostoCartIconMarkup();
    const wishlistIcon = this.#getNostoWishlistIconMarkup();
    const quickAddProductId = String(productId || productName).replace(/[^a-zA-Z0-9_-]/g, '-');
    const productHandle = this.#getNostoProductHandle(product, productUrl);

    return `
      <li
        class="predictive-search-results__card predictive-search-results__card--product nosto-autocomplete__product"
        ref="resultsItems[]"
        role="listitem"
        data-nosto-product-id="${this.#escapeAttribute(productId)}"
        data-nosto-product-url="${this.#escapeAttribute(productUrl)}"
      >
        <div
          data-nosto-autocomplete-card-shell
          data-nosto-search-query="${this.#escapeAttribute(searchTerm)}"
          data-nosto-card-url="${this.#escapeAttribute(productUrl)}"
          data-nosto-card-handle="${this.#escapeAttribute(productHandle)}"
        >
          <div class="nosto-autocomplete__product-media">
            <quick-add-component
              class="nosto-autocomplete__quick-add"
              data-product-id="${this.#escapeAttribute(quickAddProductId)}"
              data-product-title="${this.#escapeAttribute(productName)}"
              data-product-url="${this.#escapeAttribute(productUrl)}"
            >
              <button
                type="button"
                class="inline-flex h-8 w-8 items-center justify-center rounded-full bg-white text-black transition-transform hover:scale-105"
                on:click="quick-add-component/handleClick"
                aria-label="${this.#escapeAttribute(`Quick add ${productName}`)}"
              >
                ${quickAddIcon}
              </button>
              <quick-add-dialog class="quick-add-dialog relative" id="quick-add-dialog-${this.#escapeAttribute(quickAddProductId)}">
                <dialog class="dialog-modal right-modal max-w-full" ref="dialog" scroll-lock>
                  <div class="flex h-full min-h-0 flex-col" data-quick-view-modal-content></div>
                </dialog>
              </quick-add-dialog>
            </quick-add-component>
            <a
              href="${this.#escapeAttribute(productUrl)}"
              class="nosto-autocomplete__product-link group"
              data-nosto-product-id="${this.#escapeAttribute(productId)}"
              data-nosto-product-url="${this.#escapeAttribute(productUrl)}"
            >
              <span class="nosto-autocomplete__image-wrap">
                ${
                  productLabels.length
                    ? `<span class="absolute left-3 top-3 z-10 flex flex-col items-start gap-1">
                        ${productLabels
                          .map(
                            (label) => `<span class="nosto-autocomplete__badge">
                                ${this.#escapeHTML(label)}
                              </span>`
                          )
                          .join('')}
                      </span>`
                    : ''
                }
                <span class="nosto-autocomplete__wishlist" aria-hidden="true">
                  ${wishlistIcon}
                </span>
                ${
                  imageUrl
                    ? `<img
                        src="${this.#escapeAttribute(imageUrl)}"
                        alt="${this.#escapeAttribute(productName)}"
                        class="nosto-autocomplete__image"
                        loading="lazy"
                      >`
                    : ''
                }
              </span>
            </a>
          </div>
          <a
            href="${this.#escapeAttribute(productUrl)}"
            class="nosto-autocomplete__product-meta"
            data-nosto-product-id="${this.#escapeAttribute(productId)}"
            data-nosto-product-url="${this.#escapeAttribute(productUrl)}"
          >
            <span class="nosto-autocomplete__product-title title-5">${this.#escapeHTML(productName)}</span>
            ${
              productSubtitle
                ? `<span class="nosto-autocomplete__product-subtitle">${this.#escapeHTML(productSubtitle)}</span>`
                : ''
            }
            ${
              price
                ? `<span class="nosto-autocomplete__price body-regular">
                    ${showListPrice ? `<s>${this.#escapeHTML(listPrice)}</s>` : ''}
                    <span>${this.#escapeHTML(price)}</span>
                  </span>`
                : ''
            }
          </a>
        </div>
      </li>
    `;
  }

  async #hydrateNostoAutocompleteCards() {
    const resultsRoot = this.refs.predictiveSearchResults;
    if (!(resultsRoot instanceof HTMLElement)) return;

    const cardShells = Array.from(resultsRoot.querySelectorAll('[data-nosto-autocomplete-card-shell]')).filter(
      (shell) => shell instanceof HTMLElement && shell.dataset.nostoCardHydrated !== 'true'
    );

    if (!cardShells.length) return;

    await Promise.all(
      cardShells.map(async (shell) => {
        if (!(shell instanceof HTMLElement) || !shell.isConnected) return;

        const cardUrl = shell.dataset.nostoCardUrl;
        if (!cardUrl) return;

        const cacheKey = shell.dataset.nostoCardHandle || cardUrl;

        try {
          let markup = this.#nostoProductCardMarkupCache.get(cacheKey);

          if (!markup) {
            const requestUrl = new URL(cardUrl, window.location.origin);
            requestUrl.searchParams.set('section_id', 'product-card');

            const response = await fetch(requestUrl.toString());
            if (!response.ok) {
              throw new Error(`Failed to load product card section: ${response.status}`);
            }

            markup = await response.text();
            this.#nostoProductCardMarkupCache.set(cacheKey, markup);
          }

          if (!shell.isConnected) return;

          shell.innerHTML = markup;
          shell.dataset.nostoCardHydrated = 'true';

          if (window.Alpine && typeof Alpine.initTree === 'function') {
            Alpine.initTree(shell);
          }
        } catch (error) {
          console.warn('Unable to hydrate Nosto autocomplete product card.', error);
        }
      })
    );
  }

  /**
   * @param {HTMLElement} target
   */
  #recordNostoResultClick(target) {
    if (!this.#usesNostoSearch) return;

    const resultLink = target.closest('[data-nosto-product-id], [data-nosto-keyword]');
    if (!(resultLink instanceof HTMLElement)) return;

    window.nostojs((api) => {
      const keyword = resultLink.dataset.nostoKeyword;
      if (keyword && typeof api.recordSearchSubmit === 'function') {
        this.#saveRecentSearch(keyword);
        api.recordSearchSubmit(keyword);
        return;
      }

      const productId = resultLink.dataset.nostoProductId;
      const url = resultLink.dataset.nostoProductUrl || resultLink.getAttribute('href');

      if (productId && url && typeof api.recordSearchClick === 'function') {
        api.recordSearchClick('autocomplete', { productId, url });
      }
    });
  }

  /**
   * @param {string} rawQuery
   */
  #submitSearch(rawQuery) {
    const query = rawQuery.trim();
    if (!query) return;

    if (this.#usesNostoSearch) {
      window.nostojs((api) => {
        if (typeof api.recordSearchSubmit === 'function') {
          api.recordSearchSubmit(query);
        }
      });
    }

    window.location.href = this.#getSearchUrl(query);
  }

  /**
   * @param {string} currentQuery
   * @returns {string[]}
   */
  #getRecentSearches(currentQuery = '') {
    try {
      const storedValue = window.localStorage.getItem(PredictiveSearchComponent.recentSearchesStorageKey);
      if (!storedValue) return [];

      const parsed = JSON.parse(storedValue);
      if (!Array.isArray(parsed)) return [];

      return parsed.filter((query) => typeof query === 'string' && query && query !== currentQuery).slice(0, 3);
    } catch (error) {
      return [];
    }
  }

  /**
   * @param {string} query
   */
  #saveRecentSearch(query) {
    const normalizedQuery = query.trim();
    if (!normalizedQuery) return;

    try {
      const existingQueries = this.#getRecentSearches();
      const nextQueries = [normalizedQuery, ...existingQueries.filter((item) => item !== normalizedQuery)].slice(0, 3);
      window.localStorage.setItem(PredictiveSearchComponent.recentSearchesStorageKey, JSON.stringify(nextQueries));
    } catch (error) {
      // Ignore storage failures.
    }
  }

  /**
   * Sync the open-state suggestions with recent searches or configured suggestions.
   */
  #syncOpenStateRecentSearches() {
    const list = this.refs.predictiveSearchResults.querySelector('[data-search-empty-suggestions]');
    const heading = this.refs.predictiveSearchResults.querySelector('[data-search-empty-heading]');

    if (!(list instanceof HTMLElement) || !(heading instanceof HTMLElement)) return;

    const recentSearches = this.#getRecentSearches();
    const suggestions = recentSearches.length ? recentSearches : this.#getConfiguredSuggestedSearches();
    const defaultHeading = heading.dataset.defaultHeading || 'Popular Searches';
    const recentHeading = heading.dataset.recentHeading || 'Recent Searches';
    const activeHeading = recentSearches.length ? recentHeading : defaultHeading;

    heading.textContent = activeHeading;
    list.setAttribute('aria-label', activeHeading);

    list.innerHTML = suggestions.map((query) => this.#renderRecentSearch(query)).join('');
  }

  #syncMobileUtilityLinks() {
    const utilityLinks = this.refs.utilityLinks;
    if (!(utilityLinks instanceof HTMLElement)) return;

    const source = document.querySelector('[data-mobile-nav-utility-links]');
    if (!(source instanceof HTMLElement)) {
      utilityLinks.innerHTML = '';
      return;
    }

    utilityLinks.innerHTML = source.innerHTML;
    utilityLinks.querySelectorAll('a, span').forEach((element) => {
      element.classList.add('predictive-search__utility-link');
    });
  }

  /**
   * @param {string} query
   */
  #updateViewAllButton(query) {
    const viewAllButton = this.refs.viewAllButton;
    if (viewAllButton instanceof HTMLAnchorElement) {
      viewAllButton.href = this.#getSearchUrl(query);
    }
  }

  /**
   * @param {string} query
   * @returns {string}
   */
  #getSearchUrl(query) {
    const searchUrl = new URL(Theme.routes.search_url, location.origin);
    searchUrl.searchParams.set('q', query);
    return searchUrl.toString();
  }

  /**
   * @returns {string}
   */
  #getStorefrontRoot() {
    const rawRoot = this.#getStringValue(window.Shopify?.routes?.root || '/').trim();
    if (!rawRoot || rawRoot === '/') return '/';

    return `/${rawRoot.replace(/^\/+|\/+$/g, '')}/`;
  }

  /**
   * @param {string} pathname
   * @returns {boolean}
   */
  #hasLocalePrefix(pathname) {
    return /^\/[a-z]{2}(?:-[a-z]{2})?(?:\/|$)/i.test(pathname);
  }

  /**
   * @param {URL} urlObject
   * @returns {URL}
   */
  #applyStorefrontRoot(urlObject) {
    if (!(urlObject instanceof URL)) return urlObject;
    if (urlObject.origin !== location.origin) return urlObject;

    const storefrontRoot = this.#getStorefrontRoot();
    if (storefrontRoot === '/') return urlObject;
    if (urlObject.pathname.startsWith(storefrontRoot) || this.#hasLocalePrefix(urlObject.pathname)) return urlObject;

    const rootedPath = `${storefrontRoot.replace(/\/$/, '')}${urlObject.pathname}`;
    return new URL(`${rootedPath}${urlObject.search}${urlObject.hash}`, location.origin);
  }

  /**
   * @param {string} url
   * @returns {string}
   */
  #normalizeUrl(url) {
    const normalizedUrl = this.#getStringValue(url);

    if (!normalizedUrl) return '#';

    if (/^shopify:\/\//i.test(normalizedUrl)) {
      const path = `/${normalizedUrl.replace(/^shopify:\/\//i, '').replace(/^\/+/, '')}`;

      try {
        return this.#applyStorefrontRoot(new URL(path, location.origin)).toString();
      } catch (error) {
        return '#';
      }
    }

    try {
      return this.#applyStorefrontRoot(new URL(normalizedUrl, location.origin)).toString();
    } catch (error) {
      return '#';
    }
  }

  /**
   * @param {any} category
   * @returns {string}
   */
  #getNostoCategoryUrl(category) {
    const directUrl = this.#getStringValue(category?.url).trim();
    if (directUrl) return this.#normalizeUrl(directUrl);

    const urlPath = this.#getStringValue(category?.urlPath).trim();
    if (urlPath) return this.#normalizeUrl(`/${urlPath.replace(/^\/+/, '')}`);

    return '#';
  }

  /**
   * @param {any} product
   * @returns {string}
   */
  #getNostoProductUrl(product) {
    const rawUrl =
      product?.url ??
      product?.productUrl ??
      product?.canonicalUrl ??
      product?.handle ??
      this.#getNostoCustomField(product, 'url', 'handle');

    const url = this.#getStringValue(rawUrl);
    if (!url) return '#';

    if (url.startsWith('/')) return this.#normalizeUrl(url);
    if (/^https?:\/\//i.test(url)) return this.#normalizeUrl(url);
    if (url.includes('/products/')) return this.#normalizeUrl(url);

    return this.#normalizeUrl(`/products/${url}`);
  }

  /**
   * @param {any} product
   * @param {string} productUrl
   * @returns {string}
   */
  #getNostoProductHandle(product, productUrl) {
    const explicitHandle = this.#getStringValue(product?.handle ?? this.#getNostoCustomField(product, 'handle')).trim();
    if (explicitHandle) return explicitHandle;

    try {
      const normalizedUrl = new URL(productUrl, location.origin);
      const match = normalizedUrl.pathname.match(/\/products\/([^/?#]+)/i);
      return match?.[1] || '';
    } catch (error) {
      return '';
    }
  }

  /**
   * @param {any} product
   * @returns {string}
   */
  #getNostoImageUrl(product) {
    return this.#getStringValue(
      product?.imageUrl ??
        product?.thumbUrl ??
        product?.image?.url ??
        product?.image ??
        product?.thumb ??
        product?.thumbnail ??
        this.#getNostoCustomField(product, 'imageUrl')
    );
  }

  /**
   * @param {any} product
   * @param {...string} keys
   * @returns {string}
   */
  #getNostoCustomField(product, ...keys) {
    const customFields = Array.isArray(product?.customFields) ? product.customFields : [];

    for (const key of keys) {
      const match = customFields.find((field) => this.#getStringValue(field?.key).trim() === key);
      const value = this.#getStringValue(match?.value).trim();
      if (value) return value;
    }

    return '';
  }

  /**
   * @param {any} value
   * @returns {string}
   */
  #getStringValue(value) {
    if (value == null) return '';
    if (typeof value === 'string') return value;
    if (typeof value === 'number' || typeof value === 'boolean') return String(value);
    if (Array.isArray(value)) return this.#getStringValue(value[0]);
    if (typeof value === 'object') {
      return this.#getStringValue(value.url ?? value.value ?? value.handle ?? value.keyword ?? value.text);
    }

    return '';
  }

  /**
   * @param {number | string | undefined} value
   * @returns {string}
   */
  #formatNostoPrice(value) {
    const price = Number(value);
    if (!Number.isFinite(price)) return '';

    const locale = window.Shopify?.locale || document.documentElement.lang || 'en';
    const currency = window.Shopify?.currency?.active || 'GBP';

    return new Intl.NumberFormat(locale, {
      style: 'currency',
      currency,
    }).format(price);
  }

  /**
   * @param {string | number} value
   * @returns {string}
   */
  #escapeHTML(value) {
    const element = document.createElement('span');
    element.textContent = String(value ?? '');
    return element.innerHTML;
  }

  /**
   * @param {string | number} value
   * @returns {string}
   */
  #escapeAttribute(value) {
    return this.#escapeHTML(value).replace(/"/g, '&quot;');
  }

  /**
   * Fetch search results using the section renderer and update the results container.
   * @param {string} searchTerm - The term to search for
   */
  async #getSearchResults(searchTerm) {
    if (!this.dataset.sectionId) return;

    const url = new URL(Theme.routes.predictive_search_url, location.origin);
    url.searchParams.set('q', searchTerm);
    url.searchParams.set('resources[limit_scope]', 'each');

    // UNFD - Include variant SKU in product search (defaults are title, product_type, variants.title, vendor)
    url.searchParams.set('resources[options][fields]', 'author,body,title,product_type,variants.title,vendor,variants.sku');

    const { predictiveSearchResults } = this.refs;
    predictiveSearchResults.classList.remove('nosto-autocomplete');

    const abortController = this.#createAbortController();

    sectionRenderer
      .getSectionHTML(this.dataset.sectionId, false, url)
      .then((resultsMarkup) => {
        if (!resultsMarkup) return;

        if (abortController.signal.aborted) return;

        const parsedResultsMarkup = new DOMParser().parseFromString(resultsMarkup, 'text/html');
        const hasSearchData = !parsedResultsMarkup.querySelector('.predictive-search-results__no-results');

        morph(predictiveSearchResults, resultsMarkup);
        this.#toggleViewAllButton(hasSearchData);

        this.#resetScrollPositions();
        this.#syncOpenStateRecentSearches();
        this.#loadNostoTrendingPlacement();
      })
      .catch((error) => {
        if (abortController.signal.aborted) return;
        throw error;
      });
  }

  /**
   * Fetch the markup for the recently viewed products.
   * @returns {Promise<string | null>} The markup for the recently viewed products.
   */
  async #getRecentlyViewedProductsMarkup() {
    if (!this.dataset.sectionId) return null;

    const viewedProducts = RecentlyViewed.getProducts();
    if (viewedProducts.length === 0) return null;

    const url = new URL(Theme.routes.search_url, location.origin);
    url.searchParams.set('q', viewedProducts.map(/** @param {string} id */ (id) => `id:${id}`).join(' OR '));
    url.searchParams.set('resources[type]', 'product');

    return sectionRenderer.getSectionHTML(this.dataset.sectionId, false, url);
  }

  /**
   * Fetch recently viewed products using the section renderer and update the results container.
   */
  async #getRecentlyViewed() {
    const { predictiveSearchResults } = this.refs;
    // Get the initial height before the results are rendered
    const abortController = this.#createAbortController();

    try {
      const resultsMarkup = await this.#getRecentlyViewedProductsMarkup();
      if (!resultsMarkup) return;

      const parsedNextPage = new DOMParser().parseFromString(resultsMarkup, 'text/html');
      const recentlyViewedProductsHtml = parsedNextPage.getElementById('predictive-search-products');
      if (!recentlyViewedProductsHtml) return;

      for (const child of recentlyViewedProductsHtml.children) {
        if (child instanceof HTMLElement) {
          child.setAttribute('ref', 'recentlyViewedWrapper');
        }
      }

      const collectionElement = predictiveSearchResults.querySelector('#predictive-search-products');
      if (!collectionElement) return;

      if (this.refs.recentlyViewedWrapper) {
        this.refs.recentlyViewedWrapper.remove();
      }

      if (abortController.signal.aborted) return;
      // Prepend the recently viewed products to the collection
      collectionElement.prepend(...recentlyViewedProductsHtml.children);
    } catch (error) {
      throw error;
    }
  }

  #hideResetButton() {
    const { resetButton } = this.refs;

    resetButton.hidden = true;
  }

  #showResetButton() {
    const { resetButton } = this.refs;

    resetButton.hidden = false;
  }

  #hideViewAllButton() {
    const { viewAllButton, viewAllButtonWrap } = this.refs;

    if (viewAllButton instanceof HTMLAnchorElement) {
      viewAllButton.hidden = true;
    }

    if (viewAllButtonWrap instanceof HTMLElement) {
      viewAllButtonWrap.hidden = true;
    }
  }

  /**
   * @param {boolean} shouldShow
   */
  #toggleViewAllButton(shouldShow) {
    const { viewAllButton, viewAllButtonWrap } = this.refs;

    if (viewAllButton instanceof HTMLAnchorElement) {
      viewAllButton.hidden = !shouldShow;
    }

    if (viewAllButtonWrap instanceof HTMLElement) {
      viewAllButtonWrap.hidden = !shouldShow;
    }
  }

  #createAbortController() {
    const abortController = new AbortController();
    if (this.#activeFetch) {
      this.#activeFetch.abort();
    }
    this.#activeFetch = abortController;
    return abortController;
  }

  /**
   * @param {string} markup
   * @returns {Element | null}
   */
  #parseMarkup(markup) {
    if (!markup) return null;

    return new DOMParser().parseFromString(markup, 'text/html').body.firstElementChild;
  }

  #resetSearch = async () => {
    const { predictiveSearchResults, searchInput } = this.refs;
    const emptySectionId = 'predictive-search-empty';

    this.#currentIndex = -1;
    searchInput.value = '';
    this.#hideResetButton();
    this.#hideViewAllButton();
    predictiveSearchResults.classList.remove('nosto-autocomplete');

    const abortController = this.#createAbortController();
    const cachedEmptyStateMarkup = this.#emptyStateMarkup;
    const immediateEmptyState = this.#parseMarkup(cachedEmptyStateMarkup);

    if (immediateEmptyState) {
      morph(predictiveSearchResults, immediateEmptyState);
      this.#resetScrollPositions();
      this.#syncOpenStateRecentSearches();
      this.#loadNostoTrendingPlacement();
    }

    let parsedEmptySectionMarkup = this.#parseMarkup(cachedEmptyStateMarkup);

    if (!parsedEmptySectionMarkup) {
      const emptySectionMarkup = await sectionRenderer.getSectionHTML(emptySectionId, false);
      parsedEmptySectionMarkup = new DOMParser()
        .parseFromString(emptySectionMarkup, 'text/html')
        .querySelector('.predictive-search-empty-section');
    }

    if (!parsedEmptySectionMarkup) throw new Error('No empty section markup found');

    /** This needs to be awaited and not .then so the DOM is already morphed
     * when #closeResults is called and therefore the height is animated */
    const viewedProducts = RecentlyViewed.getProducts();

    if (viewedProducts.length > 0) {
      const recentlyViewedMarkup = await this.#getRecentlyViewedProductsMarkup();
      if (!recentlyViewedMarkup) return;

      const parsedRecentlyViewedMarkup = new DOMParser().parseFromString(recentlyViewedMarkup, 'text/html');
      const recentlyViewedProductsHtml = parsedRecentlyViewedMarkup.getElementById('predictive-search-products');
      if (!recentlyViewedProductsHtml) return;

      for (const child of recentlyViewedProductsHtml.children) {
        if (child instanceof HTMLElement) {
          child.setAttribute('ref', 'recentlyViewedWrapper');
        }
      }

      const collectionElement = parsedEmptySectionMarkup.querySelector('#predictive-search-products');
      if (!collectionElement) return;
      collectionElement.prepend(...recentlyViewedProductsHtml.children);
    }

    if (abortController.signal.aborted) return;

    morph(predictiveSearchResults, parsedEmptySectionMarkup);
    this.#resetScrollPositions();
    this.#syncOpenStateRecentSearches();
    this.#loadNostoTrendingPlacement();
  };

  /**
   * Closes the predictive search.
   */
  #blurSearch = () => {
    this.refs.searchInput.blur();
  };
}

if (!customElements.get('predictive-search-component')) {
  customElements.define('predictive-search-component', PredictiveSearchComponent);
}
